import React, { useEffect, useMemo, useState } from 'react';
import type { MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import styled from '@emotion/styled';
import { Background, Row, Col, Copyright, Title, Text, Icon } from '@vmos2/design-system';
import { OrderClaimScreen } from '@vmos2/loyalty';
import { useLocale, useRoute } from '@vmos2/utils';
import { useConfig, useSettings, useUser } from '@vmos2/providers';
import { storage, getDeviceInfo, getPrefixedRoute, sessionStore } from '@vmos2/core';
import { useLocation, FinderByPostcode, AddressesLookup } from '@vmos2/location';

import { usePaymentProviders } from './hooks/usePaymentProviders';
import { useStores } from './hooks/useStores';
import { Stores, Unavailable } from './components';
import { LandingSelector } from './components-v2';
import { Header } from './layout/header';
import { Footer } from './layout/footer';
import { selectors } from './selectors';

const colParams = {
  xs: 10,
  xsOffset: 1,
  sm: 6,
  smOffset: 3,
  md: 10,
  mdOffset: 1,
  lg: 8,
  lgOffset: 2,
  xl: 6,
  xlOffset: 3,
};

type Props = {
  background: MutableRefObject<HTMLDivElement | null>,
};

export const StoreSelection = ({ background }: Props) => {
  const config = useConfig();
  const settings = useSettings();
  const { t } = useTranslation('landing');
  const { t: tStoreSlection } = useTranslation('storeSelection');
  const {
    navigateTo,
    route: {
      params: { app: channel, ...params },
      path,
    },
  } = useRoute();
  const theme = useTheme();
  const [deviceType, setDeviceType] = useState(null);
  const { bootstrap } = useConfig();
  const { settings: bootstrapSettings, hostBrands } = bootstrap;
  const { paymentProviders } = usePaymentProviders();
  const { storesAvailability, getStoresMenus, clearStoreMenus, stores, getStores, menus, storesCount } =
    useStores(channel);
  const { location, geolocationKey, isGeocodeEnabled, postCode, isGoogleMapsEnabled } = useLocation();
  const { appDownloadLink, hideStores, storeSelectionAddress, driverIntegration } = settings;
  const { locale } = useLocale(config, settings);
  const {
    isGuest,
    logout,
    profile: { profileData },
    updateProfilePostcode,
  } = useUser();
  const disableUseMyLocation = !!get(storeSelectionAddress, 'disableUseMyLocation');
  const deliverooSignature = get(driverIntegration, 'deliverooSignature');

  const copyright = useMemo(
    () => selectors.copyright(bootstrapSettings, paymentProviders),
    [bootstrapSettings, paymentProviders]
  );

  const storeSelectors = useMemo(() => selectors.storeSelectors(bootstrapSettings), [bootstrapSettings]);

  const hideStoreList = useMemo(
    () => (geolocationKey || isGoogleMapsEnabled) && hideStores && !location,
    [geolocationKey, hideStores, location, isGoogleMapsEnabled]
  );

  const [title, subtitle] = useMemo(() => {
    if (storeSelectors.length === 1) {
      const { type } = storeSelectors[0];

      return [type, `${type}Subtitle`];
    }

    return ['ourStores', 'firstEnterPostcode'];
  }, [storeSelectors]);

  const isDeliveroo = useMemo(
    () => channel === 'delivery' && isGoogleMapsEnabled && !isEmpty(deliverooSignature),
    [channel, isGoogleMapsEnabled, deliverooSignature]
  );

  useEffect(() => {
    if (isGuest) {
      logout();
    }
  }, [isGuest]);

  useEffect(() => {
    if (storeSelectors.length === 1 && !path.startsWith('/claim')) {
      navigateTo('preorder.store-selection', { app: storeSelectors[0].type });
      storage.setItem('appType', storeSelectors[0].type);
    }
  }, [path, storeSelectors, navigateTo]);

  useEffect(() => {
    (async () => {
      const info = await getDeviceInfo();

      setDeviceType(info.platform);
    })();
  }, [path]);

  useEffect(() => {
    if (sessionStore.editModeOrder.get()) {
      storage.removeItem('bundles');
      storage.removeItem('basket.serviceCharge');
      storage.removeItem('basket.fixedServiceCharge');
      storage.removeItem('accessories');
      sessionStore.editModeOrder.remove();
    }
  }, []);

  const storesUUIDs = useMemo(() => map(stores, 'uuid'), [stores]);
  const hostBrandsUUIDS = useMemo(() => map(hostBrands, 'uuid'), [hostBrands]);
  const addScrollEffect = deviceType === 'android';

  return (
    <>
      <Background background={background} backgroundColor={theme.storeSelection.primaryBgColor} />

      <OrderClaimScreen
        appDownloadLink={appDownloadLink}
        closeRoute={getPrefixedRoute('store-selection')}
        locale={locale}
        navigateTo={navigateTo}
        orderUUID={params?.orderUUID}
        path={path}
      />

      <Header />

      <Content>
        <Container onTouchMove={e => e.preventDefault()}>
          <LeftSide>
            {isEmpty(storeSelectors) ? (
              <Unavailable hasSelectors={false} channel={channel} hasEnabledStores={storesAvailability} />
            ) : (
              <>
                <Row>
                  <Col xs={10} xsOffset={1} sm={10} smOffset={1} md={10} mdOffset={1} noPadding>
                    <Title level={1} margin="xsmall" align="center" mode="OurStores">
                      {t(title)}
                    </Title>

                    <Text margin="large" align="center" color={theme.color.grey}>
                      {t(
                        geolocationKey
                          ? storeSelectors.length <= 1
                            ? subtitle
                            : 'enterPostcode'
                          : 'ourStoresDescription'
                      )}
                    </Text>
                  </Col>
                </Row>

                {storeSelectors.length > 1 && (
                  <LandingSelector
                    selectors={storeSelectors}
                    stores={storesUUIDs}
                    brandUUIDS={hostBrandsUUIDS}
                    getStoresMenus={getStoresMenus}
                  />
                )}

                <Wrapper>
                  <Col {...colParams} noPadding>
                    {isGeocodeEnabled ? (
                      <AddressesLookup
                        disableUseMyLocation={disableUseMyLocation}
                        alertIfAddressNotFound
                        userAddress={get(profileData, 'address')}
                        onPostCodeChange={updateProfilePostcode}
                        addScrollEffect={addScrollEffect}
                      />
                    ) : (
                      <FinderByPostcode disableMyLocation={disableUseMyLocation} />
                    )}
                  </Col>
                </Wrapper>

                {!hideStoreList && (
                  <Stores
                    settings={bootstrapSettings}
                    hasEnabledStores={storesAvailability}
                    channel={channel}
                    location={location}
                    postCode={postCode}
                    isGeocodeEnabled={isGeocodeEnabled}
                    getStoresMenus={getStoresMenus}
                    clearStoreMenus={clearStoreMenus}
                    stores={stores}
                    getStores={getStores}
                    menus={menus}
                    storesCount={storesCount}
                    deliveryIntegration={driverIntegration}
                    brandUUIDS={hostBrandsUUIDS}
                  />
                )}

                {isDeliveroo && (
                  <DeliverooIcon>
                    <Icon name="Deliveroo" size={120} />
                  </DeliverooIcon>
                )}

                {get(copyright, 'name') && <Copyright {...copyright} />}
              </>
            )}
          </LeftSide>

          <RightSide>
            <ContentDescriptionWrapper>
              <ContentDescription>
                <MainTitle level={2} mode="StoreSelection" margin="small">
                  {tStoreSlection('landingHeroName')}
                </MainTitle>

                <MainDescription level={1} tag="span" mode="StoreSelectionDescription" margin="none">
                  {tStoreSlection('landingHeroDescription')}
                </MainDescription>
              </ContentDescription>
            </ContentDescriptionWrapper>
          </RightSide>
        </Container>
      </Content>

      <Footer />
    </>
  );
};

const Content = styled.div`
  height: ${({ theme }) => `calc(100% - ${theme.constants.headerHeight})`};
  margin-top: ${({ theme }) => theme.constants.headerHeight};

  @media (${({ theme }) => theme.breakpoint.tabletLarge}) {
    font-size: ${({ theme }) => theme.fontSize.md};
  }
`;

const Container = styled.div`
  display: grid;
  height: 100%;
  grid-template-columns: repeat(1, 1fr);

  @media (${({ theme }) => theme.breakpoint.tabletLarge}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (${({ theme }) => theme.breakpoint.mobile}) {
    grid-template-rows: min-content;
  }
`;

const LeftSide = styled('div')`
  display: flex;
  flex-direction: column;
  margin-top: ${({ theme }) => theme.constants.headerHeight};
  min-height: calc(100% - ${({ theme }) => theme.constants.headerHeight} * 2 - 200px);

  @media (${({ theme }) => theme.breakpoint.mobile}) {
    height: calc(100% - ${({ theme }) => theme.constants.headerHeight});
  }
`;

const RightSide = styled.div(
  ({ theme }) => `
  display: flex;
  min-height: 200px;
  grid-row-start: 1;

  @media (${theme.breakpoint.tabletLarge}) {
    position: fixed;
    grid-row-start: auto;
    top: ${theme.constants.headerHeight};
    right: 0;
    width: 50%;
    height: calc(100% - ${theme.constants.headerHeight});
  }
`
);

const ContentDescriptionWrapper = styled('div')`
  position: relative;
  pointer-events: none;
  display: flex;
  width: 100%;
  padding: ${({ theme }) => theme.constants.headerHeight};
  color: ${({ theme }) => theme.landingPage.descriptionColor || theme.color.black};
  align-items: ${({ theme }) => theme.landingPage.alignItems};
  background-image: url(${({ theme }) => theme.landingPage.backgroundImage || theme.storeSelection.backgroundImage});
  background-size: cover;
  background-position: center center;

  @media (${({ theme }) => theme.breakpoint.tabletLarge}) {
    padding-left: 60px;
    padding-right: 60px;
    height: 100%;
  }
`;

const ContentDescription = styled('div')`
  text-align: ${({ theme }) => theme.landingPage.textAlign};
  white-space: pre-line;
  width: 100%;
`;

const MainTitle = styled(Title)`
  color: ${({ theme }) => theme.landingPage.nameColor || theme.color.black};
`;

const MainDescription = styled(Title)`
  color: ${({ theme }) => theme.landingPage.descriptionColor || theme.color.black};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
`;

const Wrapper = styled(Row)`
  margin-bottom: 32px;
`;

const DeliverooIcon = styled('div')`
  text-align: center;
`;
